import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { Grid, SxProps, Theme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import logoImg from "../assets/logo.webp";
import burgerImg from "../assets/menu.webp";
import burgerImgMobile from "../assets/burger-mobile.webp";
import closeImg from "../assets/close.webp";
import { useIsMobile } from "../hooks";

const lngs: any = {
    en: { nativeName: "EN" },
    it: { nativeName: "IT" },
};

const HeaderStyle: Record<string, SxProps<Theme>> = {
    bar: {
        background: "transparent",
        boxShadow: "none",
    },
    burgerContainer: {
        position: "absolute",
        display: "flex",
        alignItems: "center",
        left: "30px",
    },
    burger: {
        img: {
            height: "40px",
            width: "auto",
        },
    },
    burgerMobile: {
        img: {
            height: "30px",
            width: "auto",
        },
    },
    headerLogo: {
        img: {
            height: "80px",
            width: "auto",
        },
        marginTop: "10px",
    },
    headerLogoMobile: {
        img: {
            height: "60px",
            width: "auto",
        },
        marginTop: "10px",
    },
    drawer: (theme: Theme) => ({
        minWidth: 500,
        height: "100vh",
        borderRight: "3px solid #81BC00",
    }),
    drawerPaper: (theme: Theme) => ({
        background: theme.palette.primary.main,
    }),
    drawerHeader: {
        paddingLeft: "30px",
        paddingTop: "30px",
        flexDirection: "row",
        display: "flex",
        flex: 1,
        justifyContent: "space-between",
        alignItems: "center",
    },
    drawerTitle: {
        display: "flex",
        fontSize: "18px",
        color: "#81BC00",
    },
    drawerExitButton: {
        display: "flex",
        img: {
            height: "30px",
            width: "auto",
        },
    },
    drawerLink: {},
};

export default function Header() {
    const { t, i18n } = useTranslation();

    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { pathname } = location;
    const { isMobile } = useIsMobile();

    const toggleDrawer = (newOpen: boolean) => () => {
        setOpen(newOpen);
    };

    const isCurrentPage = (link: string) => {
        if (link === "/") {
            return pathname === link;
        }
        return pathname.includes(link);
    };

    const pages = [
        { link: "/", label: "HOME" },
        { link: "/about-us", label: t("menu.about") },
        { link: "/testing", label: "TIC - TESTING, INSPECTION & CERTIFICATION" },
        { link: "/software", label: "CONNECTIVE ENGINEERING" },
        { link: "/accreditations", label: t("menu.accredcompl") },
        { link: "/news", label: "NEWS" },
        { link: "/partners", label: t("menu.partners") },
        { link: "/startup-incubator", label: "STARTUP INCUBATOR" },
        { link: "/careers", label: t("menu.careers") },
        { link: "/gallery", label: t("menu.gallery") },
        { link: "/contact", label: t("menu.contact") },
        { link: "/whistleblowing", label: t("menu.whistle") },
    ];

    const DrawerList = (
        <Box
            sx={[
                HeaderStyle.drawer,
                isMobile ? { minWidth: "350px", width: "350px" } : {},
            ].flat(2)}
            role="presentation"
            onClick={toggleDrawer(false)}
        >
            <Box sx={HeaderStyle.drawerHeader}>
                <Typography sx={HeaderStyle.drawerTitle}>Menu</Typography>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={HeaderStyle.drawerExitButton}
                    onClick={() => setOpen(false)}
                >
                    <img src={closeImg} />
                </IconButton>
            </Box>

            <List>
                {/* Selettore lingua */}
                <ListItem
                    key={"lang-selector"}
                    // component={Link}
                    // to={"#"}
                    disablePadding
                    sx={{ paddingLeft: "30px", marginBottom: "20px" }}
                >
                    <ListItemText
                        sx={{ flex: "none", cursor: "pointer"  }}
                        primary={"Eng"}
                        primaryTypographyProps={{
                            sx: { color: i18n.resolvedLanguage === "en" ? "white" : "gray" },
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            i18n.changeLanguage("en");
                        }}
                    />
                    <ListItemText
                        sx={{ flex: "none", paddingLeft: "8px", marginRight: "8px" }}
                        primary={"|"}
                    />
                    <ListItemText
                        sx={{ flex: "none", cursor: "pointer" }}
                        primaryTypographyProps={{
                            sx: { color: i18n.resolvedLanguage === "it" ? "white" : "gray" },
                        }}
                        primary={"Ita"}
                        onClick={(event) => {
                            event.stopPropagation();
                            i18n.changeLanguage("it");
                        }}
                    />
                </ListItem>

                {/* Collegamenti menu */}
                {pages.map((page) => (
                    <ListItem
                        component={Link}
                        to={page.link}
                        key={page.label}
                        disablePadding
                        sx={{
                            paddingLeft: "33px",
                            marginBottom: "10px",
                            "&:hover": {
                                background: "#36314945",
                            },
                        }}
                    >
                        <ListItemText
                            primary={page.label}
                            primaryTypographyProps={{
                                sx: [
                                    HeaderStyle.drawerLink,
                                    {
                                        textDecoration: isCurrentPage(page.link) ? "underline" : "",
                                    },
                                ].flat(2),
                            }}
                        />
                    </ListItem>
                ))}

                {/* Elemento Virtual tour (separato perché deve aprirsi in una nuova scheda) */}

                <ListItem
                    component={Link}
                    to={"https://cms.coperniko.com/tour/reinova/239/virtual-tour"}
                    target="_blank"
                    rel="noopener noreferrer"   // Security best practice
                    key={"virtualtour"}
                    disablePadding
                    sx={{
                        paddingLeft: "33px",
                        marginBottom: "10px",
                        "&:hover": {
                            background: "#36314945",
                        },
                    }}
                >
                    <ListItemText
                        primary={"VIRTUAL TOUR"}
                        primaryTypographyProps={{
                            sx: [
                                HeaderStyle.drawerLink,
                                {
                                    textDecoration: "",
                                },
                            ].flat(2),
                        }}
                    />
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 0 }}>
            <AppBar position="static" sx={HeaderStyle.bar}>
                <Toolbar sx={{ justifyContent: "center" }}>
                    <Box
                        sx={[
                            HeaderStyle.burgerContainer,
                            isMobile ? { left: "10px" } : {},
                        ].flat(2)}
                    >
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={isMobile ? HeaderStyle.burgerMobile : HeaderStyle.burger}
                            onClick={() => setOpen(!open)}
                        >
                            <img src={isMobile ? burgerImgMobile : burgerImg} />
                        </IconButton>
                        <Drawer
                            open={open}
                            onClose={toggleDrawer(false)}
                            PaperProps={{ sx: HeaderStyle.drawerPaper }}
                        >
                            {DrawerList}
                        </Drawer>
                    </Box>

                    <Box
                        sx={
                            isMobile ? HeaderStyle.headerLogoMobile : HeaderStyle.headerLogo
                        }
                    >
                        <img src={logoImg} />
                    </Box>
                </Toolbar>
            </AppBar>
        </Box>
    );
}
